<template>
  <CompanyAppLayout
    page-title="Field Ticket"
    page-subtitle="Review, approve, and invoice"
  >
    <div class="space-y-6 pt-4 max-w-4xl">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <router-link to="/company/field-tickets" class="text-slate-400 hover:text-white text-sm">← All tickets</router-link>
        <span v-if="ticket" class="status-pill" :class="'st-' + ticket.status">{{ ticket.status }}</span>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>
      <p v-if="message" class="text-emerald-400">{{ message }}</p>
      <div v-if="loading" class="text-slate-400 py-10 text-center">Loading…</div>

      <template v-else-if="ticket">
        <div class="panel">
          <div class="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <p class="text-xs text-slate-500 font-mono">{{ ticket.number }}</p>
              <h1 class="text-2xl font-bold text-white mt-1">{{ ticket.title }}</h1>
              <p class="text-slate-400 mt-1">{{ ticket.customerName || 'No customer' }} · {{ ticket.siteName || 'No site' }}</p>
            </div>
            <div class="text-right">
              <p class="text-sm text-slate-400">Total</p>
              <p class="text-3xl font-bold text-emerald-400">${{ Number(ticket.totalAmount || 0).toLocaleString() }}</p>
            </div>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 text-sm">
            <div>
              <p class="text-slate-500">Well / Location</p>
              <p class="text-white">{{ ticket.wellName || '—' }}</p>
              <p class="text-slate-400 text-xs">{{ ticket.leaseLocation || ticket.siteName || '' }}</p>
              <p class="text-slate-400 text-xs">{{ ticket.countyState || '' }}</p>
            </div>
            <div>
              <p class="text-slate-500">Company Man</p>
              <p class="text-white">{{ ticket.companyManName || '—' }}</p>
              <p class="text-slate-400 text-xs">{{ ticket.companyManEmail || '' }}</p>
            </div>
            <div>
              <p class="text-slate-500">Timeline</p>
              <p class="text-slate-300">Work date {{ ticket.workDate || '—' }}</p>
              <p class="text-slate-300">Created {{ ticket.createdAt }}</p>
              <p v-if="ticket.submittedAt" class="text-slate-300">Submitted {{ ticket.submittedAt }}</p>
              <p v-if="ticket.approvedAt" class="text-emerald-300">Approved {{ ticket.approvedAt }}</p>
            </div>
          </div>

          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
            <div><p class="text-slate-500">PO #</p><p class="text-white">{{ ticket.poNumber || '—' }}</p></div>
            <div><p class="text-slate-500">AFE #</p><p class="text-white">{{ ticket.afeNumber || '—' }}</p></div>
            <div><p class="text-slate-500">Job #</p><p class="text-white">{{ ticket.jobNumber || '—' }}</p></div>
            <div><p class="text-slate-500">Rig / Unit</p><p class="text-white">{{ ticket.rigName || '—' }}</p></div>
            <div><p class="text-slate-500">Supervisor</p><p class="text-white">{{ ticket.supervisorName || '—' }}</p></div>
            <div><p class="text-slate-500">Service</p><p class="text-white">{{ ticket.serviceType || ticket.title || '—' }}</p></div>
            <div><p class="text-slate-500">Start</p><p class="text-white">{{ ticket.startTime || '—' }}</p></div>
            <div><p class="text-slate-500">End</p><p class="text-white">{{ ticket.endTime || '—' }}</p></div>
          </div>

          <p v-if="ticket.fieldNotes || ticket.description" class="mt-4 text-slate-300 text-sm whitespace-pre-wrap">{{ ticket.fieldNotes || ticket.description }}</p>
          <p v-if="ticket.rejectionReason" class="mt-3 text-red-400 text-sm">Rejection: {{ ticket.rejectionReason }}</p>
        </div>

        <div class="panel">
          <h2 class="text-lg font-semibold text-white mb-4">Line Items</h2>
          <table class="w-full text-sm">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 text-left">Date</th>
                <th class="py-2 text-left">Employee / Equip</th>
                <th class="py-2 text-left">Description</th>
                <th class="py-2 text-right">Qty</th>
                <th class="py-2 text-right">Rate</th>
                <th class="py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in ticket.lines || []" :key="line.id" class="border-b border-slate-800 text-slate-200">
                <td class="py-2">{{ line.date || '—' }}</td>
                <td class="py-2">{{ line.employee || line.lineType }}</td>
                <td class="py-2">{{ line.description }}</td>
                <td class="py-2 text-right">{{ line.quantity }} {{ line.unit }}</td>
                <td class="py-2 text-right">${{ Number(line.rate).toLocaleString() }}</td>
                <td class="py-2 text-right text-emerald-400">${{ Number(line.amount).toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="panel space-y-4">
          <h2 class="text-lg font-semibold text-white">Actions</h2>

          <div v-if="['open','in_progress'].includes(ticket.status)" class="flex flex-wrap gap-3">
            <button class="btn-secondary" :disabled="busy" @click="doAction('submit')">Submit for Approval</button>
          </div>

          <div v-if="['open','in_progress','submitted'].includes(ticket.status)" class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">Company Man Name</label>
              <input v-model="manName" class="form-input" />
            </div>
            <div>
              <label class="form-label">Company Man Email</label>
              <input v-model="manEmail" type="email" class="form-input" />
            </div>
            <button class="btn-secondary sm:col-span-2" :disabled="busy || !manEmail" @click="requestApproval">
              Generate Company-Man Approval Link
            </button>
            <p v-if="approveUrl" class="sm:col-span-2 text-sm">
              <span class="text-slate-400">Share this link:</span>
              <a :href="approveUrl" class="text-emerald-400 break-all ml-2" target="_blank">{{ approveUrl }}</a>
              <button class="ml-3 text-slate-300 hover:text-white" @click="copyLink">Copy</button>
            </p>
          </div>

          <div v-if="ticket.status === 'submitted'" class="flex flex-wrap gap-3">
            <button class="btn-primary" :disabled="busy" @click="doAction('approve')">Approve Ticket</button>
            <button class="btn-danger" :disabled="busy" @click="rejectTicket">Reject</button>
          </div>

          <div v-if="ticket.status === 'approved'" class="flex flex-wrap gap-3">
            <button class="btn-primary" :disabled="busy" @click="doAction('invoice')">Convert to Invoice</button>
          </div>

          <div v-if="ticket.status === 'invoiced'" class="text-emerald-400 text-sm">
            Invoiced (#{{ ticket.invoiceId }}). View under Payments &amp; Invoices.
          </div>

          <div v-if="['open', 'in_progress'].includes(ticket.status)" class="flex flex-wrap gap-3">
            <button class="btn-danger" :disabled="busy" @click="deleteTicket">Delete Ticket</button>
          </div>
        </div>
      </template>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { buildApiUrl } from '@/config/api'

const route = useRoute()
const router = useRouter()
const loading = ref(true)
const busy = ref(false)
const error = ref('')
const message = ref('')
const ticket = ref<any>(null)
const manName = ref('')
const manEmail = ref('')
const approveUrl = ref('')

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`, 'Content-Type': 'application/json' }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const id = route.params.id
    const res = await fetch(buildApiUrl(`company/field-tickets/${id}`), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load')
    ticket.value = data.ticket
    manName.value = data.ticket.companyManName || ''
    manEmail.value = data.ticket.companyManEmail || ''
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function deleteTicket() {
  if (!window.confirm(`Delete field ticket ${ticket.value?.number || ''}? This cannot be undone.`)) return
  busy.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/field-tickets/${route.params.id}`), {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Delete failed')
    router.push('/company/field-tickets')
  } catch (e: any) {
    error.value = e.message || 'Delete failed'
  } finally {
    busy.value = false
  }
}

async function doAction(action: string) {
  busy.value = true
  error.value = ''
  message.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/field-tickets/${route.params.id}/${action}`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ companyManName: manName.value, signature: manName.value || 'Company approval' }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Action failed')
    ticket.value = data.ticket
    message.value = action === 'invoice'
      ? `Invoice ${data.invoiceNumber} created`
      : `Ticket ${action}d`
  } catch (e: any) {
    error.value = e.message || 'Action failed'
  } finally {
    busy.value = false
  }
}

async function requestApproval() {
  busy.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl(`company/field-tickets/${route.params.id}/request-approval`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ companyManName: manName.value, companyManEmail: manEmail.value }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed')
    ticket.value = data.ticket
    approveUrl.value = data.approveUrl
    message.value = 'Approval link ready — share with the company man'
  } catch (e: any) {
    error.value = e.message || 'Failed'
  } finally {
    busy.value = false
  }
}

async function rejectTicket() {
  const reason = prompt('Rejection reason') || 'Rejected'
  busy.value = true
  try {
    const res = await fetch(buildApiUrl(`company/field-tickets/${route.params.id}/reject`), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ reason }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Reject failed')
    ticket.value = data.ticket
    message.value = 'Ticket rejected — returned to open'
  } catch (e: any) {
    error.value = e.message || 'Reject failed'
  } finally {
    busy.value = false
  }
}

async function copyLink() {
  if (!approveUrl.value) return
  try { await navigator.clipboard.writeText(approveUrl.value) } catch { /* ignore */ }
}

onMounted(load)
</script>

<style scoped>
.panel { @apply bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6; }
.form-label { @apply block text-sm text-slate-400 mb-1; }
.form-input { @apply w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white; }
.btn-primary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium disabled:opacity-50; }
.btn-secondary { @apply inline-flex items-center px-4 py-2 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-200 disabled:opacity-50; }
.btn-danger { @apply inline-flex items-center px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/40 text-red-300 disabled:opacity-50; }
.status-pill { @apply inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize; }
.st-open { @apply bg-slate-500/20 text-slate-300; }
.st-in_progress { @apply bg-cyan-500/20 text-cyan-300; }
.st-submitted { @apply bg-amber-500/20 text-amber-300; }
.st-approved { @apply bg-emerald-500/20 text-emerald-300; }
.st-invoiced { @apply bg-violet-500/20 text-violet-300; }
</style>
